/**
 * ============================================================================
 * USER / TENANT PAGE: WhatsApp Business API v2.0 Documentation & Playground
 * ============================================================================
 * Purpose:
 *   Complete public and tenant API documentation with interactive testing console,
 *   live API key authentication, ready-to-import Postman collection download,
 *   direct Swagger UI link, comprehensive error codes table, and copy-pasteable
 *   code snippets in JavaScript, Node.js, PHP, Python, and cURL.
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { API_V2_URL, SERVER_BASE_URL } from "../../../core/config/constants";

// SVG Icons
const KeyIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
  </svg>
);

const PlayIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
  </svg>
);

const DocumentArrowDownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

const ArrowTopRightOnSquareIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);

const ClipboardDocumentCheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.97 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.09 1.976 1.05 1.976 2.188V8.25M9 13.5l2.25 2.25L15 12m-9 9h12A2.25 2.25 0 0 0 20.25 18.75V8.25A2.25 2.25 0 0 0 18 6H6A2.25 2.25 0 0 0 3.75 8.25v10.5A2.25 2.25 0 0 0 6 20.25Z" />
  </svg>
);

const ClipboardIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
  </svg>
);

const EyeIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const EyeSlashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

const ArrowPathIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ExclamationCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

// Endpoints catalog
const API_ENDPOINTS = [
  {
    id: "get-templates",
    group: "Templates",
    name: "Get All Templates",
    method: "GET",
    path: "/templates",
    description: "Fetch all approved WhatsApp message templates with language and category filters.",
    queryParams: [
      { name: "category", type: "string", description: "Filter by category: MARKETING, UTILITY, AUTHENTICATION" },
      { name: "page", type: "number", description: "Page number (default: 1)" },
      { name: "limit", type: "number", description: "Page size (default: 50)" },
    ],
    sampleBody: null,
  },
  {
    id: "create-template-text",
    group: "Templates",
    name: "Create Template (Text / Auth)",
    method: "POST",
    path: "/templates",
    description: "Create a text or authentication OTP WhatsApp template with copy code buttons.",
    sampleBody: {
      name: "auth_otp_code_v1",
      language: "en_US",
      category: "AUTHENTICATION",
      body_text: "{{1}} is your verification code. For your security, do not share this code.",
      waba_id: "28084736814530791",
      components: [
        {
          type: "BODY",
          add_security_recommendation: true,
        },
        {
          type: "FOOTER",
          code_expiration_minutes: 10,
        },
        {
          type: "BUTTONS",
          buttons: [
            {
              type: "OTP",
              otp_type: "COPY_CODE",
              text: "Copy Code",
            },
          ],
        },
      ],
    },
  },
  {
    id: "create-template-media",
    group: "Templates",
    name: "Create Template with Media Header",
    method: "POST",
    path: "/templates/media",
    description: "Create a marketing or utility template containing an image, video, or document header.",
    sampleBody: {
      name: "diwali_festival_special",
      language: "en",
      category: "MARKETING",
      format: "IMAGE",
      waba_id: "28084736814530791",
      header_handle: "h_sample_header_handle_meta_resumable_hash_4f2b",
      body_text: "Hello {{1}}, celebrate this festive season with an exclusive 40% discount on all orders! Use voucher code {{2}}.",
      example_body: ["Rajesh", "FESTIVE40"],
      footer_text: "Digiforest Communications",
      buttons: [
        {
          type: "QUICK_REPLY",
          text: "Claim Offer",
        },
        {
          type: "URL",
          text: "Shop Now",
          url: "https://example.com/shop/{{1}}",
        },
      ],
    },
  },
  {
    id: "create-template-interactive",
    group: "Templates",
    name: "Create Interactive Template (Buttons)",
    method: "POST",
    path: "/templates/interactive",
    description: "Create a template with rich interactive quick-reply buttons and website links.",
    sampleBody: {
      name: "order_confirmation_v2",
      language: "en",
      category: "UTILITY",
      waba_id: "28084736814530791",
      body_text: "Hi {{1}}, your order #{{2}} of Rs {{3}} has been confirmed and packed for delivery.",
      example_body: ["Priya", "ORD-99812", "1,499"],
      footer_text: "Thank you for shopping with us!",
      buttons: [
        {
          type: "QUICK_REPLY",
          text: "Track Shipment",
        },
        {
          type: "PHONE_NUMBER",
          text: "Call Support",
          phone_number: "+919876543210",
        },
      ],
    },
  },
  {
    id: "send-template-header",
    group: "Messaging",
    name: "Send with Text Header",
    method: "POST",
    path: "/messages/template/header",
    description: "Send a template message featuring a dynamic text header variable to a single recipient.",
    sampleBody: {
      from: "918758759200",
      to: "919876543210",
      template_name: "welcome_onboarding",
      language: "en",
      header_text: "Special VIP Member Update",
      body_params: ["Vikram Singh", "Gold Tier"],
    },
  },
  {
    id: "send-template-media",
    group: "Messaging",
    name: "Send with Media Header",
    method: "POST",
    path: "/messages/template/media",
    description: "Send a media message with image/video/document URL or header handle to a recipient.",
    sampleBody: {
      from: "918758759200",
      to: "919876543210",
      template_name: "festive_promo_2026",
      language: "en",
      media_type: "IMAGE",
      media_url: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800",
      body_params: ["Rohan", "30% OFF"],
    },
  },
  {
    id: "send-template-media-body",
    group: "Messaging",
    name: "Send with Media & Dynamic Body",
    method: "POST",
    path: "/messages/template/media-body",
    description: "Send template with media header, multiple ordered body parameters, and button payload.",
    sampleBody: {
      from: "918758759200",
      to: "919876543210",
      template_name: "event_ticket_invoice",
      language: "en",
      media_type: "DOCUMENT",
      media_url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
      body_params: ["Aman Gupta", "INV-88741", "VIP Pass"],
      buttons: [
        {
          type: "QUICK_REPLY",
          index: "0",
          payload: "confirm_attendance",
        },
      ],
    },
  },
  {
    id: "send-bulk-personalized",
    group: "Messaging",
    name: "Send Bulk Personalized (Different Params & Buttons)",
    method: "POST",
    path: "/messages/template/bulk-personalized",
    description: "Send messages to multiple numbers with individual personalized variables, media, and button payloads.",
    sampleBody: {
      from: "918758759200",
      template_name: "student_exam_card",
      language: "en",
      recipients: [
        {
          to: "919876543210",
          media_url: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800",
          body_params: ["Aarav", "Roll #101", "Room 4A"],
          buttons: [
            {
              index: "0",
              payload: "acknowledge_101",
            },
          ],
        },
        {
          to: "919123456789",
          media_url: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800",
          body_params: ["Diya", "Roll #102", "Room 4B"],
          buttons: [
            {
              index: "0",
              payload: "acknowledge_102",
            },
          ],
        },
      ],
    },
  },
  {
    id: "send-bulk-broadcast",
    group: "Messaging",
    name: "Send Bulk Broadcast (Same Params)",
    method: "POST",
    path: "/messages/template/bulk-broadcast",
    description: "Broadcast an identical message with same media & parameters to thousands of phone numbers.",
    sampleBody: {
      from: "918758759200",
      to_list: ["919876543210", "919123456789", "919811223344"],
      template_name: "urgent_office_update",
      language: "en",
      media_type: "IMAGE",
      media_url: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=800",
      body_params: ["All Team Members", "Tomorrow 10:00 AM"],
    },
  },
  {
    id: "status-health",
    group: "System",
    name: "Health & Service Status",
    method: "GET",
    path: "/status",
    description: "Check WhatsApp API Gateway connectivity, database health, and server uptime.",
    sampleBody: null,
  },
];

// Error codes reference
const ERROR_CODES_TABLE = [
  {
    code: 200,
    title: "OK / Success",
    description: "Request processed successfully. Message has been queued or dispatched to Meta / Karix.",
    action: "Inspect the `response.message_id` or `response.data` for dispatch status.",
  },
  {
    code: 400,
    title: "Bad Request",
    description: "Missing required fields (e.g. `from`, `to`, `template_name`), invalid JSON syntax, or unsupported format.",
    action: "Verify parameter types, JSON syntax, and mandatory fields in request payload.",
  },
  {
    code: 401,
    title: "Unauthorized (Invalid API Key)",
    description: "Missing `x-api-key` header or provided API Key does not exist in the database.",
    action: "Pass `x-api-key: df_live_...` in HTTP header. Copy key from the Credentials card above.",
  },
  {
    code: 402,
    title: "Payment Required / Insufficient Balance",
    description: "Account wallet balance is lower than message dispatch cost.",
    action: "Top-up wallet credit in Wallet & Ledgers menu or contact platform administrator.",
  },
  {
    code: 403,
    title: "Forbidden / Inactive Account",
    description: "Account status is suspended or IP is not in whitelist.",
    action: "Check account status with admin or verify allowed IP addresses.",
  },
  {
    code: 404,
    title: "Not Found",
    description: "Sender phone number or template name is not registered under your WhatsApp Business account.",
    action: "Check provisioned numbers and verify template approval status in Message Templates.",
  },
  {
    code: 422,
    title: "Meta WhatsApp Validation Error",
    description: "Template parameters count mismatch, invalid phone number format, or rate limit exceeded.",
    action: "Ensure phone number includes country code without '+' (e.g. 919876543210) and matches template variable count.",
  },
  {
    code: 500,
    title: "Internal Gateway Error",
    description: "Upstream telephony operator or Meta Graph API returned temporary connectivity failure.",
    action: "Retry request with exponential backoff or check operator system status.",
  },
];

export default function ApiDocumentation() {
  const [activeTab, setActiveTab] = useState("playground"); // "playground" | "endpoints" | "errors"
  const [selectedEndpoint, setSelectedEndpoint] = useState(API_ENDPOINTS[0]);
  const [selectedLanguage, setSelectedLanguage] = useState("curl"); // "curl" | "javascript" | "node" | "php" | "python"

  // Credentials
  const [accountInfo, setAccountInfo] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [senderNumbers, setSenderNumbers] = useState([]);
  const [baseUrl, setBaseUrl] = useState(API_V2_URL);
  const [, setLoadingCreds] = useState(true);


  // Playground request / response state
  const [requestMethod, setRequestMethod] = useState("GET");
  const [requestPath, setRequestPath] = useState("/templates");
  const [requestBodyText, setRequestBodyText] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  // Fetch account credentials and sender numbers
  const fetchCredentials = useCallback(async () => {
    try {
      setLoadingCreds(true);

      // 1. Determine active account ID and JWT token from localStorage
      let activeAccountId =
        localStorage.getItem("active_account_id") ||
        localStorage.getItem("digi_active_account_id");
      const token = localStorage.getItem("token") || localStorage.getItem("auth_token");

      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const u = JSON.parse(storedUser);
          if (!activeAccountId) {
            activeAccountId = u.int_account_id || u.str_account_id || u.account_id || (u.int_role_id === 3 ? u.int_user_id : null);
          }
        }
      } catch (_) { }

      const params = activeAccountId ? { accountId: activeAccountId } : {};
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (activeAccountId) headers["x-account-id"] = String(activeAccountId);

      const apiUrl = `${API_V2_URL}/docs/credentials`;
      const res = await axios.get(apiUrl, { params, headers, timeout: 10000 });
      if (res.data?.success) {
        setAccountInfo(res.data.account);
        setApiKey(res.data.account.api_key || "");
        const senders = res.data.sender_numbers || [];
        setSenderNumbers(senders);
        if (res.data.baseUrl) {
          setBaseUrl(res.data.baseUrl);
        }
      }
    } catch (err) {
      console.warn("Could not fetch credentials via public route, checking fallback...", err);
      // Fallback: derive from localStorage
      try {
        const stored = localStorage.getItem("user");
        if (stored) {
          const u = JSON.parse(stored);
          setAccountInfo({
            account_id: u.int_account_id || u.str_account_id || u._id || 1,
            company_name: u.str_name || u.str_company_name || u.str_username || "Workspace User",
            api_key: u.str_api_key || "",
            current_credit: u.int_current_credit || 500,
            status: "active",
          });
          if (u.str_api_key) setApiKey(u.str_api_key);
        }
      } catch (_) { }
    } finally {
      setLoadingCreds(false);
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  // When endpoint changes in playground, update request details
  const handleSelectEndpoint = (ep) => {
    setSelectedEndpoint(ep);
    setRequestMethod(ep.method);
    setRequestPath(ep.path);
    if (ep.sampleBody) {
      // If sample has 'from' or 'waba_id', replace with provisioned sender details
      const bodyCopy = { ...ep.sampleBody };
      if (bodyCopy.from && senderNumbers.length > 0) {
        bodyCopy.from = senderNumbers[0].phone_number;
      }
      if (senderNumbers.length > 0 && senderNumbers[0].waba_id) {
        if ("waba_id" in bodyCopy || ep.group === "Templates") {
          bodyCopy.waba_id = senderNumbers[0].waba_id;
        }
      }
      setRequestBodyText(JSON.stringify(bodyCopy, null, 2));
    } else {
      setRequestBodyText("");
    }
    setExecutionResult(null);
  };

  // Copy API Key
  const handleCopyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  // Regenerate API Key with SweetAlert confirmation
  const handleRegenerateKey = async () => {
    const confirm = await Swal.fire({
      title: "Regenerate API Key?",
      text: "Any external webhook, script, or CRM integration using the old key will stop working immediately until updated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3a57e8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Regenerate",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("auth_token");
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (accountInfo?.account_id) headers["x-account-id"] = String(accountInfo.account_id);

      const res = await axios.post(
        `${API_V2_URL}/docs/regenerate-key`,
        { accountId: accountInfo?.account_id },
        { headers }
      );

      if (res.data?.success && res.data.api_key) {
        setApiKey(res.data.api_key);
        if (accountInfo) {
          setAccountInfo({ ...accountInfo, api_key: res.data.api_key });
        }
        Swal.fire({
          icon: "success",
          title: "API Key Regenerated",
          text: "Your new API key is active now.",
          timer: 2500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Regeneration Failed",
        text: err.response?.data?.error || err.message,
      });
    }
  };

  // Execute API Request from the Playground
  const handleExecuteRequest = async () => {
    setIsExecuting(true);
    const startTime = performance.now();

    const fullUrl = `${baseUrl}${requestPath}`;
    let parsedBody = null;

    if (requestMethod !== "GET" && requestBodyText.trim()) {
      try {
        parsedBody = JSON.parse(requestBodyText);
      } catch (e) {
        setIsExecuting(false);
        setExecutionResult({
          status: 400,
          statusText: "Invalid JSON in Request Body",
          durationMs: 0,
          data: {
            error: "JSON Parsing Error: Please verify syntax of the request body.",
            details: e.message,
          },
        });
        return;
      }
    }

    try {
      const response = await axios({
        method: requestMethod,
        url: fullUrl,
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey || "df_live_test",
        },
        data: parsedBody,
        timeout: 15000,
      });

      const endTime = performance.now();
      setExecutionResult({
        status: response.status,
        statusText: response.statusText || "OK",
        durationMs: Math.round(endTime - startTime),
        headers: response.headers,
        data: response.data,
      });
    } catch (err) {
      const endTime = performance.now();
      setExecutionResult({
        status: err.response?.status || 500,
        statusText: err.response?.statusText || "Request Failed",
        durationMs: Math.round(endTime - startTime),
        headers: err.response?.headers || {},
        data: err.response?.data || { error: err.message },
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Generate code snippet according to selected language
  const generatedCode = useMemo(() => {
    const ep = selectedEndpoint;
    const url = `${baseUrl}${ep.path}`;
    const key = apiKey || "df_live_YOUR_API_KEY";
    const bodyObj = ep.sampleBody;
    const bodyJson = bodyObj ? JSON.stringify(bodyObj, null, 2) : null;

    switch (selectedLanguage) {
      case "curl": {
        if (ep.method === "GET") {
          return `curl -X GET "${url}" \\
  -H "x-api-key: ${key}" \\
  -H "Accept: application/json"`;
        }
        return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${key}" \\
  -d '${bodyJson}'`;
      }

      case "javascript": {
        if (ep.method === "GET") {
          return `// Browser Fetch (JavaScript)
const response = await fetch("${url}", {
  method: "GET",
  headers: {
    "x-api-key": "${key}",
    "Accept": "application/json"
  }
});

const data = await response.json();
console.log("Response:", data);`;
        }
        return `// Browser Fetch (JavaScript)
const payload = ${bodyJson};

const response = await fetch("${url}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "x-api-key": "${key}"
  },
  body: JSON.stringify(payload)
});

const data = await response.json();
console.log("Message Response:", data);`;
      }

      case "node": {
        if (ep.method === "GET") {
          return `// Node.js (axios)
const axios = require("axios");

async function run() {
  try {
    const res = await axios.get("${url}", {
      headers: {
        "x-api-key": "${key}"
      }
    });
    console.log("Status:", res.status);
    console.log("Data:", res.data);
  } catch (err) {
    console.error("API Error:", err.response?.data || err.message);
  }
}

run();`;
        }
        return `// Node.js (axios)
const axios = require("axios");

async function sendMessage() {
  const payload = ${bodyJson};

  try {
    const res = await axios.post("${url}", payload, {
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "${key}"
      }
    });
    console.log("Message Queued:", res.data);
  } catch (err) {
    console.error("API Error:", err.response?.data || err.message);
  }
}

sendMessage();`;
      }

      case "php": {
        if (ep.method === "GET") {
          return `<?php
// PHP cURL Implementation
$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => '${url}',
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_ENCODING => '',
  CURLOPT_MAXREDIRS => 10,
  CURLOPT_TIMEOUT => 30,
  CURLOPT_FOLLOWLOCATION => true,
  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
  CURLOPT_CUSTOMREQUEST => 'GET',
  CURLOPT_HTTPHEADER => array(
    'x-api-key: ${key}',
    'Accept: application/json'
  ),
));

$response = curl_exec($curl);
$err = curl_error($curl);
curl_close($curl);

if ($err) {
  echo "cURL Error #:" . $err;
} else {
  $data = json_decode($response, true);
  print_r($data);
}`;
        }
        return `<?php
// PHP cURL Implementation
$payload = ${bodyJson};

$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => '${url}',
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_ENCODING => '',
  CURLOPT_MAXREDIRS => 10,
  CURLOPT_TIMEOUT => 30,
  CURLOPT_FOLLOWLOCATION => true,
  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
  CURLOPT_CUSTOMREQUEST => 'POST',
  CURLOPT_POSTFIELDS => json_encode($payload),
  CURLOPT_HTTPHEADER => array(
    'Content-Type: application/json',
    'x-api-key: ${key}'
  ),
));

$response = curl_exec($curl);
$httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
$err = curl_error($curl);
curl_close($curl);

if ($err) {
  echo "cURL Error #:" . $err;
} else {
  echo "HTTP Code: " . $httpCode . "\\n";
  print_r(json_decode($response, true));
}`;
      }

      case "python": {
        if (ep.method === "GET") {
          return `# Python 3 (requests)
import requests

url = "${url}"
headers = {
    "x-api-key": "${key}",
    "Accept": "application/json"
}

response = requests.get(url, headers=headers)
print("Status Code:", response.status_code)
print("Response JSON:", response.json())`;
        }
        return `# Python 3 (requests)
import requests
import json

url = "${url}"
headers = {
    "Content-Type": "application/json",
    "x-api-key": "${key}"
}
payload = ${bodyJson ? bodyJson.replace(/true/g, "True").replace(/false/g, "False").replace(/null/g, "None") : "{}"}

response = requests.post(url, headers=headers, json=payload)
print("Status Code:", response.status_code)
print("Response:", response.json())`;
      }

      default:
        return "";
    }
  }, [selectedEndpoint, selectedLanguage, baseUrl, apiKey]);

  // Copy code snippet
  const handleCopySnippet = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  // Open Swagger in new window
  const handleOpenSwagger = () => {
    window.open(`${API_V2_URL}/docs/swagger`, "_blank", "noopener,noreferrer");
  };

  // Download Postman Collection
  const handleDownloadPostman = () => {
    window.location.href = `${API_V2_URL}/docs/postman?api_key=${apiKey || ""}`;
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Banner Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight">
            WhatsApp Business API Documentation
          </h1>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleDownloadPostman}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-medium text-sm transition-all duration-150 shadow-md shadow-orange-600/30 cursor-pointer"
            title="Download ready-to-import Postman collection"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            <span>Download Postman</span>
          </button>

          <button
            onClick={handleOpenSwagger}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm transition-all duration-150 shadow-md shadow-emerald-600/30 cursor-pointer"
            title="Open Swagger OpenAPI interactive UI"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            <span>Swagger UI Docs</span>
          </button>
        </div>
      </div>

      {/* API Key & Authentication Credentials Card */}
      <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-[#3a57e8]">
              <KeyIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  API Authentication Credentials
                </h2>
                {accountInfo?.company_name && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-blue-50 dark:bg-blue-900/40 text-[#3a57e8] border border-blue-200 dark:border-blue-800">
                    {accountInfo.company_name} (Acc #{accountInfo.account_id})
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Authenticate all requests using the <code className="text-xs font-mono px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[#3a57e8]">x-api-key</code> HTTP request header.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-xs text-gray-500 dark:text-gray-400">Wallet Balance</div>
              <div className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400">
                ₹ {Number(accountInfo?.current_credit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </div>
            </div>
            <button
              onClick={handleRegenerateKey}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors cursor-pointer"
              title="Generate a new API key"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
              <span>Regenerate Key</span>
            </button>
          </div>
        </div>

        {/* API Key Box */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
          <div className="lg:col-span-8 flex items-center gap-2 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-2.5">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
              API Key:
            </span>
            <input
              type={showKey ? "text" : "password"}
              readOnly
              value={apiKey || "Generating..."}
              className="flex-1 bg-transparent border-none text-xs font-mono font-semibold text-gray-900 dark:text-white outline-none select-all"
            />
            <button
              onClick={() => setShowKey(!showKey)}
              className="p-1 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
              title={showKey ? "Hide key" : "Show key"}
            >
              {showKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
            <button
              onClick={handleCopyKey}
              className="flex items-center gap-1 px-3 py-1 bg-[#3a57e8] hover:bg-[#2e47d4] text-white rounded-lg text-xs font-medium shadow-xs transition-colors cursor-pointer"
            >
              {copiedKey ? (
                <>
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <ClipboardIcon className="w-3.5 h-3.5" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>

          <div className="lg:col-span-4 flex items-center justify-between lg:justify-end gap-3 text-xs bg-gray-50 dark:bg-gray-900/40 p-2.5 rounded-xl border border-gray-200 dark:border-gray-800">
            <div>
              <span className="text-gray-500">Base URL: </span>
              <span className="font-mono font-bold text-gray-900 dark:text-gray-200">{baseUrl}</span>
            </div>
            <span className="px-2 py-0.5 rounded-md font-bold text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              Active
            </span>
          </div>
        </div>

        {/* Sender numbers list badge */}
        {senderNumbers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">Registered Sender Numbers:</span>
            {senderNumbers.map((s, idx) => (
              <span
                key={idx}
                className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-[#3a57e8] border border-blue-200 dark:border-blue-800"
              >
                +{s.phone_number}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={() => setActiveTab("playground")}
          className={`flex items-center gap-2 pb-3 px-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${activeTab === "playground"
              ? "border-[#3a57e8] text-[#3a57e8]"
              : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
        >
          <PlayIcon className="w-4 h-4" />
          <span>Interactive API Playground</span>
        </button>

        <button
          onClick={() => setActiveTab("endpoints")}
          className={`flex items-center gap-2 pb-3 px-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${activeTab === "endpoints"
              ? "border-[#3a57e8] text-[#3a57e8]"
              : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
        >
          <ClipboardDocumentCheckIcon className="w-4 h-4" />
          <span>Endpoints & Code Generator</span>
        </button>

        <button
          onClick={() => setActiveTab("errors")}
          className={`flex items-center gap-2 pb-3 px-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${activeTab === "errors"
              ? "border-[#3a57e8] text-[#3a57e8]"
              : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
        >
          <ExclamationCircleIcon className="w-4 h-4" />
          <span>HTTP Error Codes Reference</span>
        </button>
      </div>

      {/* TAB 1: INTERACTIVE API PLAYGROUND */}
      {activeTab === "playground" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Endpoint Picker */}
          <div className="lg:col-span-4 lg:sticky lg:top-4 self-start space-y-3">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Select Route to Test
              </span>
              <span className="text-[11px] font-mono font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/80 px-2 py-0.5 rounded-full">
                {API_ENDPOINTS.length} Routes
              </span>
            </div>
            <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-2 space-y-1 shadow-sm h-[480px] lg:h-[540px] overflow-y-auto overscroll-contain">
              {API_ENDPOINTS.map((ep) => {
                const isSelected = selectedEndpoint.id === ep.id;
                return (
                  <button
                    key={ep.id}
                    onClick={() => handleSelectEndpoint(ep)}
                    className={`w-full text-left p-2.5 rounded-xl transition-all cursor-pointer flex flex-col gap-1 ${isSelected
                        ? "bg-[#3a57e8] text-white shadow-md shadow-[#3a57e8]/30"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800/60 text-gray-700 dark:text-gray-300"
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold truncate">{ep.name}</span>
                      <span
                        className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${isSelected
                            ? "bg-white/20 text-white"
                            : ep.method === "GET"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                              : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
                          }`}
                      >
                        {ep.method}
                      </span>
                    </div>
                    <span
                      className={`text-[11px] font-mono truncate ${isSelected ? "text-blue-100" : "text-gray-400"
                        }`}
                    >
                      {ep.path}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Column: Interactive Tester Console */}
          <div className="lg:col-span-8 lg:sticky lg:top-4 self-start space-y-4">
            <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4 max-h-[calc(100vh-100px)] overflow-y-auto overscroll-contain">
              {/* Endpoint Header & URL input */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span>{selectedEndpoint.name}</span>
                    <span className="text-xs font-normal text-gray-500">
                      ({selectedEndpoint.group})
                    </span>
                  </h3>
                  <button
                    onClick={() => handleSelectEndpoint(selectedEndpoint)}
                    className="text-xs text-[#3a57e8] hover:underline cursor-pointer"
                  >
                    Reset Sample Payload
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedEndpoint.description}
                </p>
              </div>

              {/* URL bar + Method Badge + Send Button */}
              <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl p-1.5">
                <span
                  className={`text-xs font-mono font-bold px-3 py-2 rounded-lg ${requestMethod === "GET"
                      ? "bg-emerald-500 text-white"
                      : "bg-[#3a57e8] text-white"
                    }`}
                >
                  {requestMethod}
                </span>

                <div className="flex-1 flex items-center font-mono text-xs px-2 truncate">
                  <span className="text-gray-400">{baseUrl}</span>
                  <input
                    type="text"
                    value={requestPath}
                    onChange={(e) => setRequestPath(e.target.value)}
                    className="flex-1 bg-transparent font-mono text-gray-900 dark:text-white font-semibold outline-none px-1"
                  />
                </div>

                <button
                  onClick={handleExecuteRequest}
                  disabled={isExecuting}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#3a57e8] hover:bg-[#2e47d4] text-white text-xs font-bold transition-all shadow-md shadow-[#3a57e8]/30 cursor-pointer disabled:opacity-50"
                >
                  {isExecuting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-3.5 h-3.5" />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>

              {/* Request Headers Preview */}
              <div className="text-xs bg-gray-50 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-200 dark:border-gray-800">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">
                  Request Headers:
                </span>
                <div className="font-mono text-gray-700 dark:text-gray-300 mt-1 flex flex-wrap gap-3">
                  <span>x-api-key: {apiKey ? apiKey.substring(0, 15) + "••••••••" : "df_live_..."}</span>
                  <span>Content-Type: application/json</span>
                  <span>Accept: application/json</span>
                </div>
              </div>

              {/* Request Body Editor (if POST) */}
              {requestMethod !== "GET" && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                      Request Body (JSON):
                    </label>
                    <span className="text-[10px] text-gray-400">
                      Edit numbers or parameters before dispatching
                    </span>
                  </div>
                  <textarea
                    rows={10}
                    value={requestBodyText}
                    onChange={(e) => setRequestBodyText(e.target.value)}
                    placeholder='{"key": "value"}'
                    className="w-full bg-[#1e293b] text-emerald-400 font-mono text-xs p-4 rounded-xl border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3a57e8] leading-relaxed shadow-inner"
                    spellCheck="false"
                  />
                </div>
              )}

              {/* Execution Result Viewer */}
              {executionResult && (
                <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                        Live Response:
                      </span>
                      <span
                        className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${executionResult.status >= 200 && executionResult.status < 300
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                            : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                          }`}
                      >
                        {executionResult.status} {executionResult.statusText}
                      </span>
                      <span className="text-xs font-mono text-gray-400">
                        ({executionResult.durationMs} ms)
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(executionResult.data, null, 2));
                        Swal.fire({
                          toast: true,
                          position: "top-end",
                          icon: "success",
                          title: "Copied Response JSON",
                          showConfirmButton: false,
                          timer: 1500,
                        });
                      }}
                      className="text-xs text-[#3a57e8] hover:underline cursor-pointer"
                    >
                      Copy Response
                    </button>
                  </div>

                  <pre className="bg-[#0f172a] text-blue-300 font-mono text-xs p-4 rounded-xl border border-gray-800 overflow-auto max-h-[320px] leading-relaxed">
                    {JSON.stringify(executionResult.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: ENDPOINTS & CODE GENERATOR */}
      {activeTab === "endpoints" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Sidebar list of endpoints */}
            <div className="lg:col-span-4 lg:sticky lg:top-4 self-start space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  API Methods
                </span>
                <span className="text-[11px] font-mono font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/80 px-2 py-0.5 rounded-full">
                  {API_ENDPOINTS.length} Routes
                </span>
              </div>
              <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-2 space-y-1 shadow-sm h-[480px] lg:h-[540px] overflow-y-auto overscroll-contain">
                {API_ENDPOINTS.map((ep) => {
                  const isSelected = selectedEndpoint.id === ep.id;
                  return (
                    <button
                      key={ep.id}
                      onClick={() => handleSelectEndpoint(ep)}
                      className={`w-full text-left p-3 rounded-xl transition-all cursor-pointer ${isSelected
                          ? "bg-[#3a57e8] text-white shadow-md shadow-[#3a57e8]/30"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800/60 text-gray-700 dark:text-gray-300"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold">{ep.name}</span>
                        <span
                          className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${isSelected
                              ? "bg-white/20 text-white"
                              : ep.method === "GET"
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
                            }`}
                        >
                          {ep.method}
                        </span>
                      </div>
                      <span
                        className={`text-[11px] font-mono block mt-1 ${isSelected ? "text-blue-100" : "text-gray-400"
                          }`}
                      >
                        {ep.path}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Side: Code Generator & Endpoint Specs */}
            <div className="lg:col-span-8 lg:sticky lg:top-4 self-start space-y-5 max-h-[calc(100vh-100px)] overflow-y-auto overscroll-contain">
              {/* Overview Details */}
              <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs font-mono font-bold px-3 py-1 rounded-md ${selectedEndpoint.method === "GET"
                        ? "bg-emerald-500 text-white"
                        : "bg-[#3a57e8] text-white"
                      }`}
                  >
                    {selectedEndpoint.method}
                  </span>
                  <span className="text-sm font-mono font-bold text-gray-900 dark:text-white">
                    {baseUrl}{selectedEndpoint.path}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {selectedEndpoint.description}
                </p>

                {/* Query Params Table if exists */}
                {selectedEndpoint.queryParams && (
                  <div className="pt-2">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                      Query Parameters:
                    </span>
                    <div className="mt-1 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-800/60 font-semibold text-gray-500">
                          <tr>
                            <th className="p-2.5">Parameter</th>
                            <th className="p-2.5">Type</th>
                            <th className="p-2.5">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {selectedEndpoint.queryParams.map((q, idx) => (
                            <tr key={idx} className="font-mono">
                              <td className="p-2.5 font-bold text-[#3a57e8]">{q.name}</td>
                              <td className="p-2.5 text-gray-500">{q.type}</td>
                              <td className="p-2.5 font-sans text-gray-700 dark:text-gray-300">
                                {q.description}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Code Snippet Box with Language Tabs */}
              <div className="bg-[#0f172a] rounded-2xl border border-gray-800 shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-[#1e293b]/70 border-b border-gray-800">
                  {/* Language Selector Buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[
                      { id: "curl", label: "cURL" },
                      { id: "javascript", label: "JavaScript" },
                      { id: "node", label: "Node.js" },
                      { id: "php", label: "PHP (cURL)" },
                      { id: "python", label: "Python (requests)" },
                    ].map((lang) => (
                      <button
                        key={lang.id}
                        onClick={() => setSelectedLanguage(lang.id)}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${selectedLanguage === lang.id
                            ? "bg-[#3a57e8] text-white shadow-xs"
                            : "text-gray-400 hover:text-white hover:bg-gray-800"
                          }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleCopySnippet}
                    className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    {copiedSnippet ? (
                      <>
                        <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400">Copied!</span>
                      </>
                    ) : (
                      <>
                        <ClipboardIcon className="w-3.5 h-3.5" />
                        <span>Copy Code</span>
                      </>
                    )}
                  </button>
                </div>

                <pre className="p-5 font-mono text-xs text-emerald-300 overflow-x-auto leading-relaxed max-h-[480px]">
                  {generatedCode}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: ERROR CODES & TROUBLESHOOTING */}
      {activeTab === "errors" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              Standard HTTP Status & API Error Codes
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Digiforest WhatsApp API returns standard HTTP status codes along with a JSON body indicating failure cause and actionable resolution steps.
            </p>
          </div>

          <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/80 font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                  <tr>
                    <th className="p-4 w-28">Status</th>
                    <th className="p-4 w-48">Error Type</th>
                    <th className="p-4">Description / Cause</th>
                    <th className="p-4">Recommended Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-sans">
                  {ERROR_CODES_TABLE.map((err, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                      <td className="p-4">
                        <span
                          className={`font-mono font-bold px-2.5 py-1 rounded-md text-xs ${err.code === 200
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                              : err.code === 401 || err.code === 403
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                : err.code === 402
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300"
                                  : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                            }`}
                        >
                          {err.code}
                        </span>
                      </td>
                      <td className="p-4 font-bold text-gray-900 dark:text-white">
                        {err.title}
                      </td>
                      <td className="p-4 text-gray-600 dark:text-gray-300 leading-relaxed">
                        {err.description}
                      </td>
                      <td className="p-4 text-gray-500 dark:text-gray-400 font-medium">
                        {err.action}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
